import { useCallback, useMemo } from "react"
import { Box, ToggleButtonGroup, useTheme } from "@mui/material"
import { CalendarStarIcon } from "components/icons"

import {
  CURRENT_EVENT_FILTER_VALUE,
  UPCOMING_EVENT_FILTER_VALUE,
} from "constant"

import { getIcon } from "utils/icon"
import { useTranslation } from "providers/i18n"
import { useEvent } from "hooks/event/useEvent"

import { PageCard, PageCardContent } from "components/card"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { VenueSelector } from "components/VenueSelector"
import { EmptyData } from "components/EmptyData"

import {
  FeatureCarouselLayout,
  FeatureGridLayout,
} from "components/domains/features"

import { CategoryFilterChip } from "components/CategoryFilterBar"
import { SidebarMenu } from "components/navigation/SidebarMenu"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { IContentWithAsideProps } from "layouts/kiosk-h/ContentWithAside"
import clsx from "clsx"
import { useNavigate } from "react-router-dom"
import { Id } from "@turf/helpers"
import { VerticalEventCard } from "components/domains/events/kiosk-h/VerticalEventCard"

interface IEventsKioskHPageProps {
  Layout?: React.FC<IContentWithAsideProps>
  AppNavigation?: React.ComponentType
}

export const EventsKioskHPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
}: IEventsKioskHPageProps) => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const {
    isMultiVenueProject,
    venueFilter,
    venues,
    locationVenueId,
    groupEvents,
    activeGroupEvent,
    events,
    handleChange,
    changeVenueFilter,
    layout: eventLayoutType,
    displayMode: eventDisplayMode,
  } = useEvent()

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  const handleClickCard = useCallback(
    (id: Id) => {
      navigate(`/events/${id}`)
    },
    [navigate]
  )

  const theme = useTheme()

  const { eventCarouselOption } =
    theme.components[`EventPage`]?.defaultProps || {}

  const { styleOverrides } = theme.components?.[`EventPage`] || {}
  const {
    pageCard,
    pageCardContent,
    carouselContainer,
    carousel,
    emptyDataWrapper,
    venueSelector,
    eventGroupSelectorButton,
    gridContainer,
  } = styleOverrides

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.events) || <CalendarStarIcon />,
    [theme]
  )

  const displayEvents = useMemo(() => {
    return eventDisplayMode === "all" ? events : groupEvents[activeGroupEvent]
  }, [eventDisplayMode, events, groupEvents, activeGroupEvent])

  return (
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent
          sx={pageCardContent}
          className={clsx(
            `EventLayoutType-${eventLayoutType}`,
            `EventDisplayMode-${eventDisplayMode}`
          )}
        >
          {displayEvents && displayEvents.length > 0 ? (
            eventLayoutType === "carousel" ? (
              <FeatureCarouselLayout
                sx={carouselContainer}
                features={displayEvents}
                carouselOption={eventCarouselOption}
                carouselSx={carousel}
                onClickCard={handleClickCard}
                cardComponent={VerticalEventCard}
              />
            ) : (
              <FeatureGridLayout
                sx={gridContainer}
                features={displayEvents}
                onClickCard={handleClickCard}
                cardComponent={VerticalEventCard}
              />
            )
          ) : (
            <Box sx={emptyDataWrapper}>
              <EmptyData
                icon={emptyIcon}
                primary={t("No Current Events")}
                secondary={t("Check Back Soon")}
              />
            </Box>
          )}

          {isMultiVenueProject && (
            <Box sx={venueSelector}>
              <VenueSelector
                selectedVenue={venueFilter}
                venues={venues}
                onClickVenue={changeVenueFilter}
                currentVenue={locationVenueId}
              />
            </Box>
          )}

          {eventDisplayMode === "grouped" && (
            <Box sx={eventGroupSelectorButton}>
              <ToggleButtonGroup
                value={activeGroupEvent}
                onChange={handleChange}
                exclusive
              >
                <CategoryFilterChip value={CURRENT_EVENT_FILTER_VALUE}>
                  {t("Current Events")}
                </CategoryFilterChip>
                <CategoryFilterChip value={UPCOMING_EVENT_FILTER_VALUE}>
                  {t("Upcoming Events")}
                </CategoryFilterChip>
              </ToggleButtonGroup>
            </Box>
          )}
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
